package com.htmlagent.site.state.chat

import androidx.compose.runtime.Stable
import com.htmlagent.shared.ApiRoutes
import com.htmlagent.shared.session.ArtifactVersionSummary
import com.htmlagent.shared.session.HistoryMessageResponse
import com.htmlagent.shared.session.ImageUpload
import com.htmlagent.shared.session.SessionSummaryResponse
import com.htmlagent.shared.session.SessionUsageResponse
import com.htmlagent.shared.stream.StreamEvent
import com.htmlagent.shared.stream.TurnStage
import com.htmlagent.site.api.AgentApi
import com.htmlagent.site.api.AgentStream
import com.htmlagent.site.api.Resend
import com.htmlagent.site.i18n.AppLocale
import com.htmlagent.site.i18n.asMessage
import com.htmlagent.site.api.ApiFailure
import com.htmlagent.shared.ApiErrorCode
import com.htmlagent.site.state.Identity
import kotlin.js.Date
import kotlin.js.dateLocaleOptions
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.SharingStarted
import com.htmlagent.shared.UiLocale
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * State of the chat screen: it accumulates what arrives over the stream and exposes it as flows
 * the UI paints. It decides nothing about business; that is in the backend.
 *
 * The state is split across several `StateFlow`s and each panel collects only its own, inside the
 * component that uses it: that way a text token does not recompose the whole screen.
 *
 * The burst events (text and HTML) only accumulate and are published at most once per frame with
 * `requestAnimationFrame`. A `setTimeout(16)` does not line up with painting and sometimes
 * publishes twice between frames and sometimes not at all.
 *
 * `@Stable` is a promise, and every public property here is a `StateFlow` with not one plain
 * field among them, which is what makes it true. It tells Compose that anything taking a
 * presenter can be skipped when nothing else about the call changed; without it the runtime has
 * to assume the object could change behind its back, so every token that arrives re-runs the
 * header and the composer along with the thread.
 */
@Stable
class ChatPresenter(
    private val client: AgentApi,
    private val scope: CoroutineScope,
    private val artifactBaseUrl: String,
    /** Where the API answers, for the saved photos the thread paints straight from it. */
    private val apiBaseUrl: String = "",
) {

    private val _state = MutableStateFlow<ChatUiState>(ChatUiState.Connecting)
    val state: StateFlow<ChatUiState> = _state.asStateFlow()

    private val _messages = MutableStateFlow<List<ChatMessage>>(emptyList())
    val messages: StateFlow<List<ChatMessage>> = _messages.asStateFlow()

    private val _activity = MutableStateFlow<Activity>(Activity.Idle)
    val activity: StateFlow<Activity> = _activity.asStateFlow()

    private val _artifact = MutableStateFlow<ArtifactState>(ArtifactState.None)
    val artifact: StateFlow<ArtifactState> = _artifact.asStateFlow()

    private val _versions = MutableStateFlow<List<ArtifactVersionSummary>>(emptyList())
    val versions: StateFlow<List<ArtifactVersionSummary>> = _versions.asStateFlow()

    private val _title = MutableStateFlow<String?>(null)
    val title: StateFlow<String?> = _title.asStateFlow()

    /**
     * What this session has consumed, and what it is worth.
     *
     * Null until it is known: a session that has not run yet is not a session that cost zero,
     * and the header says nothing rather than saying "0" about something it has not measured.
     */
    private val _usage = MutableStateFlow<SessionUsageResponse?>(null)
    val usage: StateFlow<SessionUsageResponse?> = _usage.asStateFlow()

    private val _sessions = MutableStateFlow<List<SessionSummaryResponse>>(emptyList())
    val sessions: StateFlow<List<SessionSummaryResponse>> = _sessions.asStateFlow()

    /**
     * When the session was opened, kept as an instant and not as text: the format depends on
     * the language, and text frozen at open time would keep the old format after a switch.
     */
    private val _startedAtInstant = MutableStateFlow<Date?>(null)

    val startedAt: StateFlow<String> = combine(_startedAtInstant, AppLocale.current) { instant, locale ->
        instant?.let { shortTime(it, locale) }.orEmpty()
    }.stateIn(scope, SharingStarted.Eagerly, "")

    /** The last request, which is what the retry button retries. */
    private val _guestLimitReached = MutableStateFlow(false)

    /** A guest just ran out of turns: the page asks for their email instead of only saying so. */
    val guestLimitReached: StateFlow<Boolean> = _guestLimitReached.asStateFlow()

    fun dismissGuestLimit() {
        _guestLimitReached.value = false
    }

    private val _lastPrompt = MutableStateFlow<String?>(null)
    val lastPrompt: StateFlow<String?> = _lastPrompt.asStateFlow()

    private var artifactId: String? = null
    private var stream: AgentStream? = null

    private val _currentSessionId = MutableStateFlow<String?>(null)

    /**
     * The open session, so it can be marked in the list.
     *
     * A flow and not a plain field, which is what it used to be: Compose cannot observe a
     * `var`, so opening a session from the history loaded it and left the row unmarked until
     * something else happened to repaint the panel. Every other piece of state here is a flow;
     * this one was the exception, and the exception was the bug.
     */
    val currentSessionId: StateFlow<String?> = _currentSessionId.asStateFlow()

    /** The same field, for the terse reads and writes inside this class. */
    private var sessionId: String?
        get() = _currentSessionId.value
        set(value) {
            _currentSessionId.value = value
        }

    /** What is being accumulated from the turn in progress. */
    private val agentText = StringBuilder()
    private val artifactHtml = StringBuilder()
    private val progress = ArtifactProgress()

    /**
     * The HTML of each version, for the code view. It fills up over the stream; what is missing
     * is asked of the API in [fetchCodeIfMissing], never of the artifact origin.
     */
    private val htmlByVersion = mutableMapOf<Int, String>()

    private var turnStartedAt = 0.0

    /** The photos of the last request sent from here, so asking again sends them too. */
    private var lastImages: List<ImageUpload> = emptyList()

    /** The download notice is news once. See [noteDownload]. */
    private var downloadNoted = false
    private var writingArtifact = false

    /** The stage the turn last announced, which a finished tool hands the indicator back to. */
    private var stage: TurnStage? = null
    private var flushPending = false

    /** Whether this turn got as far as a saved page, and whether the person asked it to stop. */
    private var producedArtifact = false
    private var cancelRequested = false

    /**
     * Whether the turn set out to build. Loading a skill or looking for a photograph is what
     * separates "the page did not come out" from "they asked something and got an answer".
     */
    private var attemptedBuild = false

    /**
     * Starts the screen without creating any session: if one was remembered it is reopened and,
     * if not, the session is born with the first request in [send]. Creating it here left an
     * empty conversation in the database on every reload.
     */
    fun start() {
        blank()

        scope.launch {
            refreshSessions()
            val remembered = Identity.lastSession()
            if (remembered != null && openSession(remembered)) return@launch
            _state.value = ChatUiState.Ready
        }
    }

    /** Leaves the screen blank, without touching the identity or the history. */
    private fun blank() {
        stream?.close()
        stream = null
        sessionId = null
        artifactId = null
        htmlByVersion.clear()
        downloadNoted = false
        resetTurn()
        _messages.value = emptyList()
        _artifact.value = ArtifactState.None
        _versions.value = emptyList()
        _title.value = null
        _lastPrompt.value = null
        _usage.value = null
        _activity.value = Activity.Idle
        _startedAtInstant.value = Date()
    }

    /** Starts a genuinely new conversation: the previous one is forgotten, not deleted. */
    fun newSession() {
        Identity.forgetSession()
        blank()
        _state.value = ChatUiState.Ready
    }

    /**
     * Creates the session on the server. It is only called when there is something to say.
     *
     * Returns whether it worked: the caller needs to know before sending the message.
     */
    private suspend fun createSession(): Boolean =
        client.startSession()
            .onRight { session ->
                sessionId = session.sessionId
                artifactId = session.artifactId
                Identity.rememberSession(session.sessionId)
                _startedAtInstant.value = Date()
                _state.value = ChatUiState.Ready
                listen(session.sessionId)
            }
            .onLeft { failure -> _state.value = ChatUiState.Failed(failure.asMessage()) }
            .isRight()

    fun dispose() {
        stream?.close()
        stream = null
    }

    /**
     * Sends a request. The session is created here if it does not exist yet: it is the first
     * request that justifies it, not opening the screen. The user's turn is painted before the
     * 202 so their own text does not take time to appear.
     */
    fun send(prompt: String, images: List<ImageUpload> = emptyList()) {
        if (_activity.value != Activity.Idle || prompt.isBlank()) return

        _messages.value = _messages.value + ChatMessage(
            ChatMessage.Author.USER,
            prompt,
            images = images.map { "data:${it.mediaType};base64,${it.base64}" },
        )
        _activity.value = Activity.Thinking
        _lastPrompt.value = prompt
        lastImages = images

        resetTurn()
        turnStartedAt = Date.now()

        scope.launch {
            val session = sessionId
                ?: (if (createSession()) sessionId else null)
                ?: run {
                    _activity.value = Activity.Idle
                    return@launch
                }

            client.sendMessage(session, prompt, AppLocale.current.value, images)
                .onRight { refreshSessions() }
                .onLeft { failure ->
                    _activity.value = Activity.Idle
                    appendAgentMessage(failure.asMessage())
                    if (failure.isGuestLimit()) _guestLimitReached.value = true
                }
        }
    }

    /** Asks for the same thing again. The previous request stays in the history: nothing is lost. */
    fun retry() {
        _lastPrompt.value?.let { send(it, lastImages) }
    }

    /**
     * Stops the turn in flight.
     *
     * It only moves to [Activity.Stopping] as an acknowledgement; idle arrives with the stream
     * event. Jumping ahead would leave the screen saying it finished while the server keeps
     * writing.
     */
    fun cancel() {
        val session = sessionId ?: return
        cancelRequested = true
        _activity.value = Activity.Stopping
        scope.launch { client.cancel(session) }
    }

    /** Opens an earlier session and leaves the screen as it was. */
    fun open(id: String) {
        if (id == sessionId) return
        scope.launch { openSession(id) }
    }

    /**
     * Loads the session and replaces the screen. The old stream is closed before opening the new
     * one: two live `EventSource`s would paint the previous session on top of this one.
     *
     * The title comes from the person's first message, by the same rule as the list on the left,
     * and not from the page's `<title>`: on reopening, that HTML is not in memory, and the empty
     * state line above a conversation with versions would say the opposite of what is happening.
     */
    private suspend fun openSession(id: String): Boolean =
        client.session(id)
                .onRight { detail ->
                    stream?.close()
                    stream = null
                    sessionId = detail.sessionId
                    artifactId = detail.artifactId
                    Identity.rememberSession(detail.sessionId)
                    htmlByVersion.clear()
                    resetTurn()

                    _messages.value = detail.messages.map { it.restored(detail.sessionId, apiBaseUrl) }
                    _activity.value = Activity.Idle
                    _artifact.value = ArtifactState.None
                    // The authoritative figure: the consumption event is live-only and a
                    // reconnection never replays it.
                    _usage.value = detail.usage

                    // What "ask again" repeats. It is only ever set when a message is sent, so
                    // on a session rebuilt from the API it was null and the button under the
                    // last turn did nothing at all - no request, no notice, nothing on screen.
                    // The last request and not the first: it is the one that produced the page
                    // being looked at, while the title keeps the first, which is what named the
                    // conversation.
                    _lastPrompt.value = detail.messages.lastOrNull { it.role == "user" }?.text
                    // Saved photos exist only as URLs here; asking again goes without them.
                    lastImages = emptyList()

                    _title.value = detail.messages
                        .firstOrNull { it.role == "user" }
                        ?.text
                        ?.asTitle()
                    _startedAtInstant.value = Date()
                    _state.value = ChatUiState.Ready

                    // Only what is new: the conversation comes from history and replay would duplicate it.
                    listen(detail.sessionId, liveOnly = true)
                    refreshVersions()
                    showLatestVersion()
                }
                .onLeft {
                    // The remembered session may have been deleted elsewhere: forget it.
                    Identity.forgetSession()
                }
                .isRight()

    /**
     * Rewrites one of the person's messages: everything after it goes, pages included, and the
     * new text is sent as a fresh turn. The thread is cut and the turn painted before the answer,
     * as [send] does, so the stream finds the screen already waiting for it.
     */
    fun editMessage(message: ChatMessage, text: String, keptImages: List<Int> = message.images.indices.toList()) {
        val session = sessionId ?: return
        val id = message.id ?: return
        val trimmed = text.trim()
        val nothingNew = trimmed == message.text && keptImages == message.images.indices.toList()
        if (_activity.value != Activity.Idle || trimmed.isEmpty() || nothingNew) return

        _messages.value = _messages.value.before(id) +
            ChatMessage(ChatMessage.Author.USER, trimmed, images = keptImages.mapNotNull(message.images::getOrNull))
        _activity.value = Activity.Thinking
        _lastPrompt.value = trimmed
        lastImages = emptyList()
        resetTurn()
        turnStartedAt = Date.now()

        scope.launch {
            client.rewind(session, id, Resend(trimmed, AppLocale.current.value, keptImages))
                .onRight { afterRewind() }
                .onLeft { failure ->
                    _activity.value = Activity.Idle
                    // Whether the cut happened is the server's to say: the thread is read again.
                    reloadMessages(session)
                    appendAgentMessage(failure.asMessage())
                    if (failure.isGuestLimit()) _guestLimitReached.value = true
                }
        }
    }

    /**
     * Takes the conversation back to just before one of the person's messages: it, every turn
     * after it and the pages those turns wrote.
     */
    fun deleteMessage(message: ChatMessage) {
        val session = sessionId ?: return
        val id = message.id ?: return
        if (_activity.value != Activity.Idle) return

        scope.launch {
            client.rewind(session, id)
                .onRight {
                    _messages.value = _messages.value.before(id)
                    _lastPrompt.value = _messages.value.lastOrNull { it.author == ChatMessage.Author.USER }?.text
                    afterRewind()
                }
                .onLeft { failure -> appendAgentMessage(failure.asMessage()) }
        }
    }

    /**
     * The pages written after the cut are gone, and their numbers will be handed out again, so
     * the code kept for them is dropped and the panel goes back to the latest one left.
     */
    private suspend fun afterRewind() {
        htmlByVersion.clear()
        refreshSessions()
        val session = sessionId ?: return
        client.versions(session).onRight { history ->
            _versions.value = history.versions
            // A turn already writing its page owns the panel; it must not be pulled back.
            if (_artifact.value is ArtifactState.Writing) return@onRight
            val latest = history.versions.maxByOrNull { it.version }
            if (latest == null) _artifact.value = ArtifactState.None else showVersion(latest.version)
        }
    }

    /** The thread as the server keeps it, for when the screen can no longer be trusted. */
    private suspend fun reloadMessages(session: String) {
        client.session(session).onRight { detail ->
            _messages.value = detail.messages.map { it.restored(detail.sessionId, apiBaseUrl) }
        }
    }

    /**
     * Gives the turn just finished the identifiers the server saved it under, which the stream
     * does not carry. One request per turn, and only for what is still missing one.
     */
    private fun attachIds() {
        val session = sessionId ?: return
        if (_messages.value.none { it.id == null && !it.tip }) return
        scope.launch {
            client.session(session).onRight { detail ->
                _messages.value = withSavedIds(_messages.value, detail.messages)
            }
        }
    }

    /**
     * Deletes a session.
     *
     * If it was the open one, the screen is left blank without creating another: a conversation
     * that no longer exists on the server would fail on the next message without explaining
     * anything. A delete failure is said in the chat; silently, the person cannot tell whether it
     * is the screen or the server.
     */
    fun delete(id: String) {
        scope.launch {
            client.deleteSession(id)
                .onRight {
                    refreshSessions()
                    if (id == sessionId) newSession()
                }
                .onLeft { failure -> appendAgentMessage(failure.asMessage()) }
        }
    }

    /**
     * Changes the version being viewed. It generates nothing: the download still points at the
     * specific version on screen.
     */
    fun showVersion(version: Int) {
        val artifact = artifactId ?: return
        _artifact.value = ArtifactState.Ready(
            version = version,
            label = labelOf(version),
            url = "$artifactBaseUrl/a/$artifact/$version",
            html = htmlByVersion[version].orEmpty(),
        )
        fetchCodeIfMissing(version)
    }

    /** The label the history gave the version; the bare number only until the history arrives. */
    private fun labelOf(version: Int): String =
        _versions.value.firstOrNull { it.version == version }?.label ?: version.toString()

    /**
     * Asks the API for the code of a version that did not arrive over the stream, only when it is
     * missing and only the one being viewed: fetching every version when opening a session would
     * be an N+1 of hundreds of kilobytes each.
     */
    private fun fetchCodeIfMissing(version: Int, onLoaded: (String) -> Unit = {}) {
        val session = sessionId ?: return
        val artifact = artifactId ?: return
        if (!htmlByVersion[version].isNullOrEmpty()) return

        scope.launch {
            val html = client.versionCode(session, version).getOrNull() ?: return@launch
            htmlByVersion[version] = html
            onLoaded(html)
            // Only if the same one is still on screen: the user may have switched version meanwhile.
            val current = _artifact.value
            if (current is ArtifactState.Ready && current.version == version) {
                _artifact.value = current.copy(html = html)
            }
        }
    }

    private fun listen(session: String, liveOnly: Boolean = false) {
        stream?.close()
        stream = client.openStream(
            sessionId = session,
            onEvent = ::onEvent,
            onLost = { appendAgentMessage(AppLocale.strings.errors.connectionLost) },
            liveOnly = liveOnly,
        )
    }

    /**
     * Translates every stream event into state. The `when` is exhaustive and has no `else`: a new
     * backend event stops compiling instead of being ignored silently. The two burst events only
     * accumulate and ask for a frame; the rest publish directly.
     */
    private fun onEvent(event: StreamEvent) {
        when (event) {
            is StreamEvent.TextDelta -> {
                agentText.append(event.text)
                scheduleFlush()
            }

            is StreamEvent.ArtifactDelta -> {
                artifactHtml.append(event.html)
                progress.consume(event.html)
                scheduleFlush()
            }

            StreamEvent.ArtifactStart -> {
                artifactHtml.clear()
                progress.reset()
                writingArtifact = true
                _activity.value = Activity.WritingPage
                _artifact.value = ArtifactState.Writing(title = null, sections = emptyList())
            }

            is StreamEvent.ArtifactDone -> onArtifactDone(event.version, event.label)

            is StreamEvent.ToolStart -> {
                attemptedBuild = true
                _activity.value = Activity.UsingTool(event.tool, event.subject)
            }

            // Back to the stage the tool was part of, not to a generic "thinking".
            is StreamEvent.ToolDone -> _activity.value = stage?.let(Activity::InStage) ?: Activity.Thinking

            is StreamEvent.Stage -> enterStage(event.stage)

            // Already the session's running total, so it is assigned and never added to.
            is StreamEvent.Usage -> _usage.value = event.usage

            StreamEvent.Done -> finishTurn()

            is StreamEvent.Failed -> {
                flushNow()
                writingArtifact = false
                _activity.value = Activity.Idle
                // A half-written page is discarded: the panel cannot keep truncated HTML.
                val message = event.reason.asMessage()
                if (_artifact.value is ArtifactState.Writing) {
                    _artifact.value = ArtifactState.Discarded(message)
                }
                appendAgentMessage(message)
                attachIds()
            }
        }
    }

    /** Asking to stop outranks whatever the turn says it is doing meanwhile. */
    /** A version was saved: the panel shows it and the turn gets its receipt. */
    private fun onArtifactDone(version: Int, label: String) {
        val artifact = artifactId ?: return
        // The end of the artifact does not wait for a frame.
        flushNow()
        writingArtifact = false
        val html = artifactHtml.toString()
        if (html.isNotEmpty()) htmlByVersion[version] = html
        producedArtifact = true
        attemptedBuild = true
        _artifact.value = ArtifactState.Ready(
            version = version,
            label = label,
            // The iframe points at the artifact origin, never a blob or `srcdoc`: that breaks isolation.
            url = "$artifactBaseUrl/a/$artifact/$version",
            html = html,
        )
        // A section edit sends no page over the stream: its code and its card come from the API.
        if (html.isEmpty()) {
            fetchCodeIfMissing(version) { saved -> attachArtifactCard(version, label, saved) }
        } else {
            attachArtifactCard(version, label, html, progress.sections())
        }
        refreshVersions()
    }

    private fun enterStage(next: TurnStage) {
        stage = next
        if (_activity.value != Activity.Stopping) _activity.value = Activity.InStage(next)
    }

    /**
     * Closes the turn: the last message stops streaming, keeps how long it took, and says so if
     * nothing came out of it.
     *
     * A turn that produced no page used to be painted exactly like one that did — same avatar,
     * same prose, only a missing copy button — so a failure read as a success with less on it.
     * Cancelling is not counted: whoever pressed stop already knows why there is no page. Nor is
     * a turn that never used a tool: asking who the agent is gets an answer and no page, and
     * saying that nothing came out turns a correct reply into a failure.
     */
    private fun finishTurn() {
        flushNow()
        writingArtifact = false
        // A turn with no page (a cancellation) also changes the activity date and the list order.
        scope.launch { refreshSessions() }

        val seconds = elapsedSeconds()
        // A turn that never used a tool was answering, not building: there is no page missing.
        val empty = attemptedBuild && !producedArtifact && !cancelRequested
        _messages.value = _messages.value.map { message ->
            if (message.streaming) {
                message.copy(streaming = false, seconds = seconds, noPage = empty)
            } else {
                message
            }
        }
        // The skeleton has nothing left to draw and would sit there half-ticked for ever.
        if (empty && _artifact.value is ArtifactState.Writing) _artifact.value = ArtifactState.None
        _activity.value = Activity.Idle
        attachIds()
    }

    private fun scheduleFlush() {
        if (flushPending) return
        flushPending = true
        window.requestAnimationFrame {
            flushPending = false
            flushNow()
        }
    }

    private fun flushNow() {
        if (agentText.isNotEmpty()) {
            val text = agentText.toString()
            val last = _messages.value.lastOrNull()
            _messages.value = if (last?.author == ChatMessage.Author.AGENT && last.streaming) {
                _messages.value.dropLast(1) + last.copy(text = text)
            } else {
                _messages.value + ChatMessage(ChatMessage.Author.AGENT, text, streaming = true)
            }
        }
        if (writingArtifact) {
            _artifact.value = ArtifactState.Writing(progress.title, progress.sections())
            progress.title?.let { if (_title.value != it) _title.value = it }
        }
    }

    /** The turn's receipt, hung off the agent message that produced it. */
    /**
     * The receipt under the turn. Given to the last agent message that has none, so a card that
     * arrives after the API answers still lands on the turn that produced it.
     */
    private fun attachArtifactCard(
        version: Int,
        label: String,
        html: String,
        sections: List<SectionProgress> = ArtifactProgress().apply { consume(html) }.sections(),
    ) {
        val at = _messages.value.indexOfLast { it.author == ChatMessage.Author.AGENT && !it.tip }
        if (at < 0 || _messages.value[at].artifact != null) return
        val card = ArtifactCard(
            version = version,
            label = label,
            sections = sections.map { it.label },
            sizeInBytes = html.length,
        )
        _messages.value = _messages.value.mapIndexed { i, message ->
            if (i == at) message.copy(artifact = card) else message
        }
    }

    /**
     * Asks for the version history once per turn, when it ends: one request per version would be
     * the textbook N+1.
     */
    private fun refreshVersions() {
        val session = sessionId ?: return
        scope.launch {
            client.versions(session).onRight { _versions.value = it.versions }
            // The session list shows how many versions each one has.
            refreshSessions()
        }
    }

    private suspend fun refreshSessions() {
        client.sessions().onRight { _sessions.value = it.sessions }
    }

    /** After reopening, the latest version is shown: it is what the person left on screen. */
    private suspend fun showLatestVersion() {
        val session = sessionId ?: return
        client.versions(session).onRight { history ->
            _versions.value = history.versions
            history.versions.maxByOrNull { it.version }?.let { showVersion(it.version) }
        }
    }

    /**
     * The page has been downloaded: say what just happened, and ask for nothing until it has.
     *
     * Once per session. Repeating it on every download would turn the one moment the product
     * asks for something into noise, and the second time it is no longer news.
     */
    fun noteDownload() {
        if (downloadNoted) return
        downloadNoted = true
        _messages.value = _messages.value +
            ChatMessage(ChatMessage.Author.AGENT, AppLocale.strings.chat.downloaded, tip = true)
    }

    private fun appendAgentMessage(text: String) {
        _messages.value = _messages.value + ChatMessage(ChatMessage.Author.AGENT, text)
    }

    private fun resetTurn() {
        agentText.clear()
        artifactHtml.clear()
        progress.reset()
        writingArtifact = false
        stage = null
        producedArtifact = false
        attemptedBuild = false
        cancelRequested = false
    }

    private fun elapsedSeconds(): Int? =
        if (turnStartedAt == 0.0) null else ((Date.now() - turnStartedAt) / MILLIS_PER_SECOND).toInt()

    /**
     * The hour, as the language in force writes it: 24 hours in Spanish, 12 with AM/PM in
     * English. The browser decides, which is why it is not composed by hand.
     */
    private fun shortTime(date: Date, locale: UiLocale): String = date.toLocaleTimeString(
        locale.tag,
        dateLocaleOptions {
            hour = "2-digit"
            minute = "2-digit"
        },
    )
}

/**
 * What fits in the title of a reopened session. It is the same cut as `TITLE_CHARS` in
 * `SessionSummaryQuery`, so the header and the list do not show two different titles.
 */
/** Milliseconds in a second: the turn receipt is shown in seconds. */
private const val MILLIS_PER_SECOND = 1000

private const val MAX_TITLE_CHARS = 90

/**
 * A request turned into a one-line title. Line breaks are collapsed because the header is a
 * single line with an ellipsis; a request with lists used to overflow it.
 */
private fun String.asTitle(): String? = split('\n')
    .joinToString(" ") { it.trim() }
    .replace(Regex("\\s+"), " ")
    .trim()
    .take(MAX_TITLE_CHARS)
    .takeIf { it.isNotBlank() }

/**
 * Matches the thread on screen with the history the server kept, and hands each message its id.
 *
 * By role and text, in order, each saved message used once: the thread also holds what the server
 * never keeps — failures, product notices — and those simply stay without an id, which leaves
 * them copyable and nothing more.
 */
internal fun withSavedIds(shown: List<ChatMessage>, saved: List<HistoryMessageResponse>): List<ChatMessage> {
    val taken = shown.mapNotNull { it.id }.toMutableSet()
    return shown.map { message ->
        if (message.id != null || message.tip || message.streaming) return@map message
        val role = if (message.author == ChatMessage.Author.USER) "user" else "assistant"
        val match = saved.firstOrNull { it.id !in taken && it.role == role && it.text.trim() == message.text.trim() }
            ?: return@map message
        taken += match.id
        message.copy(id = match.id)
    }
}

/** A saved message as the thread paints it, its photos fetched from the API by position. */
private fun HistoryMessageResponse.restored(sessionId: String, apiBaseUrl: String): ChatMessage = ChatMessage(
    author = if (role == "user") ChatMessage.Author.USER else ChatMessage.Author.AGENT,
    text = text,
    id = id,
    restored = true,
    images = List(imageCount) { position -> apiBaseUrl + ApiRoutes.Sessions.messageImage(sessionId, id, position) },
)

/** The thread up to, and without, the message [id]: what is left after rewinding to it. */
private fun List<ChatMessage>.before(id: Long): List<ChatMessage> {
    val at = indexOfFirst { it.id == id }
    return if (at < 0) this else take(at)
}

private fun ApiFailure.isGuestLimit(): Boolean =
    this is ApiFailure.Problem && problem.code == ApiErrorCode.GUEST_LIMIT_REACHED
